import { db } from "@/services/firebaseService";
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query,
  orderBy,
  limit, 
  startAfter, 
  updateDoc, 
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
  DocumentReference
} from "firebase/firestore";
import { UserInvitation } from "@/types/userInvitation";
import { generateUniqueToken } from "./tokenService";
import CacheService from "./cacheService";

const INVITATIONS_COLLECTION = "userInvitations";
// Debug flag to control logging - set to false in production
const DEBUG_LOGGING = false;
// Cache TTL for invitations - 5 minutes
const INVITATION_CACHE_TTL = 5 * 60 * 1000;

// In-memory cache for token validation to avoid redundant queries
const tokenValidationCache = new Map<string, { 
  invitation: UserInvitation | null; 
  timestamp: number 
}>();

/**
 * Create a new user invitation
 */
export async function createInvitation(
  organizationID: string, 
  role: "camp_manager" | "camp_contributor", 
  createdByUserId: string,
  expirationDays: number = 14,
  type: "user_invite" | "location_edit" = "user_invite",
  locationAccessGroupId?: string
): Promise<UserInvitation> {
  try {
    const now = Timestamp.now();
    
    // Calculate expiration date
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + expirationDays);
    
    // Create document references
    const organizationRef = doc(db, "campOrganizations", organizationID);
    const userRef = doc(db, "users", createdByUserId);
    
    // Generate a unique 32-char hex token and use it as document ID
    const token = await generateUniqueToken();
    const invitationRef = doc(db, INVITATIONS_COLLECTION, token);
    const invitationData: UserInvitation = {
      organization: {
        id: organizationID,
        path: organizationRef.path
      },
      role: role,
      ...(locationAccessGroupId ? { locationAccessGroupId } : {}),
      token: token, // Store the token for backward compatibility (will be removed later)
      createdAt: now,
      createdBy: userRef,
      sentAt: null,
      lastViewedAt: null,
      willExpireAt: Timestamp.fromDate(expirationDate),
      status: "unsent",
      acceptedAt: null,
      resultingUser: null,
      type: type,
      id: invitationRef.id,
      viewCount: 0
    };
    
    await setDoc(invitationRef, invitationData);
    if (DEBUG_LOGGING) console.log(`Created invitation with document ID as token: ${invitationRef.id}`);
    
    // Clear any cache for token validation
    clearInvitationCache(invitationRef.id);
    
    return invitationData;
  } catch (error) {
    console.error("Error creating invitation:", error);
    throw error;
  }
}

/**
 * Update an existing invitation
 */
export async function updateInvitation(
  invitationId: string, 
  updateData: Partial<UserInvitation>
): Promise<boolean> {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    await updateDoc(invitationRef, updateData);
    if (DEBUG_LOGGING) console.log(`Updated invitation ${invitationId}:`, updateData);
    
    // Get the invitation to clear cache by token
    const invitationDoc = await getDoc(invitationRef);
    if (invitationDoc.exists()) {
      const token = invitationDoc.data().token;
      if (token) {
        clearInvitationCache(token);
      }
    }
    
    // Clear cache entries
    CacheService.clear(`invitation:${invitationId}`);
    
    return true;
  } catch (error) {
    console.error("Error updating invitation:", error);
    throw error;
  }
}

/**
 * Mark an invitation as sent
 */
export async function markInvitationAsSent(invitationId: string): Promise<boolean> {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    await updateDoc(invitationRef, {
      status: "sent",
      sentAt: Timestamp.now()
    });
    if (DEBUG_LOGGING) console.log(`Marked invitation ${invitationId} as sent`);
    
    // Clear caches
    CacheService.clear(`invitation:${invitationId}`);
    
    // Get token to clear token cache
    const invitationDoc = await getDoc(invitationRef);
    if (invitationDoc.exists()) {
      const token = invitationDoc.data().token;
      if (token) {
        clearInvitationCache(token);
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error marking invitation as sent:", error);
    throw error;
  }
}

/**
 * Mark an invitation as viewed
 */
export async function markInvitationAsViewed(token: string): Promise<boolean> {
  try {
    if (DEBUG_LOGGING) console.log(`Attempting to mark invitation with token ${token} as viewed`);
    
    // Direct document lookup since token is now the document ID
    const invitationRef = doc(db, INVITATIONS_COLLECTION, token);
    const invitationSnap = await getDoc(invitationRef);
    
    if (!invitationSnap.exists()) {
      if (DEBUG_LOGGING) console.log(`No invitation found with token ${token}`);
      return false;
    }
    
    if (DEBUG_LOGGING) console.log(`Found invitation with ID ${invitationSnap.id}, updating lastViewedAt and viewCount`);
    
    // Get the current data to increment viewCount
    const currentData = invitationSnap.data();
    const currentViewCount = currentData.viewCount || 0;
    
    await updateDoc(invitationRef, {
      lastViewedAt: Timestamp.now(),
      viewCount: currentViewCount + 1
    });
    
    // Update the cached invitation
    clearInvitationCache(token);
    
    if (DEBUG_LOGGING) console.log(`Successfully updated lastViewedAt and viewCount for invitation ${invitationSnap.id}`);
    return true;
  } catch (error) {
    console.error("Error marking invitation as viewed:", error);
    throw error;
  }
}

/**
 * Clear the view history for an invitation (last viewed time and view count)
 */
export async function clearViewHistory(invitationId: string): Promise<boolean> {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    await updateDoc(invitationRef, {
      lastViewedAt: null,
      viewCount: 0
    });
    if (DEBUG_LOGGING) console.log(`Cleared view history for invitation ${invitationId}`);
    
    // Clear caches
    CacheService.clear(`invitation:${invitationId}`);
    
    // Get token to clear token cache
    const invitationDoc = await getDoc(invitationRef);
    if (invitationDoc.exists()) {
      const token = invitationDoc.data().token;
      if (token) {
        clearInvitationCache(token);
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error clearing view history:", error);
    throw error;
  }
}

/**
 * Accept an invitation and link it to a user
 */
export async function acceptInvitation(
  token: string, 
  userId: string
): Promise<boolean> {
  try {
    if (DEBUG_LOGGING) console.log(`Accepting invitation with token ${token} for user ${userId}`);
    
    // Try to get from cache first
    let invitation = getCachedInvitation(token);
    
    // If not in cache, fetch from database
    if (!invitation) {
      invitation = await getInvitationByToken(token);
      if (!invitation) {
        if (DEBUG_LOGGING) console.log(`No invitation found with token ${token}`);
        return false;
      }
    }
    
    // Create a user reference for the resultingUser field
    const userRef = doc(db, "users", userId);
    
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitation.id);
    await updateDoc(invitationRef, {
      status: "accepted",
      acceptedAt: Timestamp.now(),
      resultingUser: {
        id: userId,
        path: userRef.path
      }
    });
    
    // Clear caches
    clearInvitationCache(token);
    CacheService.clear(`invitation:${invitation.id}`);
    
    if (DEBUG_LOGGING) console.log(`Successfully accepted invitation ${invitation.id}`);
    return true;
  } catch (error) {
    console.error("Error accepting invitation:", error);
    throw error;
  }
}

/**
 * Delete an invitation
 */
export async function deleteInvitation(invitationId: string): Promise<boolean> {
  try {
    // Get token before deletion to clear cache
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    const invitationDoc = await getDoc(invitationRef);
    let token;
    
    if (invitationDoc.exists()) {
      const data = invitationDoc.data();
      // For migrated invitations, the document ID is the token
      // For old invitations, use the token field if it exists
      token = data.token || invitationId;
    } else {
      // If document doesn't exist, use the ID as token for cache clearing
      token = invitationId;
    }
    
    // Delete the invitation
    await deleteDoc(invitationRef);
    if (DEBUG_LOGGING) console.log(`Deleted invitation ${invitationId}`);
    
    // Clear caches
    CacheService.clear(`invitation:${invitationId}`);
    if (token) {
      clearInvitationCache(token);
    }
    
    return true;
  } catch (error) {
    console.error("Error deleting invitation:", error);
    throw error;
  }
}

/**
 * Helper function to get cached invitation by token
 */
function getCachedInvitation(token: string): UserInvitation | null {
  // Check in-memory cache
  const cachedEntry = tokenValidationCache.get(token);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < INVITATION_CACHE_TTL) {
    return cachedEntry.invitation;
  }
  
  return null;
}

/**
 * Helper function to clear invitation cache
 */
function clearInvitationCache(token: string): void {
  tokenValidationCache.delete(token);
}

/**
 * Get an invitation by token (now using document ID as token)
 * This is a critical function that should minimize database reads
 */
export async function getInvitationByToken(token: string): Promise<UserInvitation | null> {
  try {
    // First check in-memory cache
    const cachedInvitation = getCachedInvitation(token);
    if (cachedInvitation) {
      if (DEBUG_LOGGING) console.log(`Using cached invitation for token ${token}`);
      return cachedInvitation;
    }
    
    if (DEBUG_LOGGING) console.log(`Fetching invitation with token (document ID): ${token}`);
    
    // Direct document lookup since token is now the document ID
    const invitationRef = doc(db, INVITATIONS_COLLECTION, token);
    const invitationSnap = await getDoc(invitationRef);
    
    if (!invitationSnap.exists()) {
      if (DEBUG_LOGGING) console.log(`No invitation found with token ${token}`);
      // Cache the negative result
      tokenValidationCache.set(token, { invitation: null, timestamp: Date.now() });
      return null;
    }
    
    const data = invitationSnap.data();
    if (DEBUG_LOGGING) console.log(`Found invitation with ID ${invitationSnap.id}, status: ${data.status}`);
    
    // Ensure type is set - default to "user_invite" if not specified
    if (!data.type) {
      data.type = "user_invite"; // Default value
    }
    
    const invitation = { 
      id: invitationSnap.id, 
      ...data 
    } as UserInvitation;
    
    // Cache the result
    tokenValidationCache.set(token, { invitation, timestamp: Date.now() });
    
    return invitation;
  } catch (error) {
    console.error(`Error getting invitation by token ${token}:`, error);
    throw error;
  }
}

/**
 * Get an invitation by id
 */
export async function getInvitationById(id: string): Promise<UserInvitation | null> {
  try {
    // Check cache first
    const cacheKey = `invitation:${id}`;
    const cachedInvitation = CacheService.get<UserInvitation>(cacheKey);
    if (cachedInvitation) {
      return cachedInvitation;
    }
    
    if (DEBUG_LOGGING) console.log(`Fetching invitation by ID: ${id}`);
    const invitationRef = doc(db, INVITATIONS_COLLECTION, id);
    const invitationSnap = await getDoc(invitationRef);
    
    if (!invitationSnap.exists()) {
      if (DEBUG_LOGGING) console.log(`No invitation found with ID ${id}`);
      return null;
    }
    
    if (DEBUG_LOGGING) console.log(`Found invitation with ID ${id}`);
    const invitation = { id: invitationSnap.id, ...invitationSnap.data() } as UserInvitation;
    
    // Cache the result
    CacheService.set(cacheKey, invitation, INVITATION_CACHE_TTL);
    
    return invitation;
  } catch (error) {
    console.error("Error getting invitation by id:", error);
    throw error;
  }
}

/**
 * Get paginated invitations
 */
export async function getPaginatedInvitations(
  pageSize: number = 20,
  lastVisible: QueryDocumentSnapshot<DocumentData> | null = null,
  sortField: string = "createdAt",
  sortDirection: "asc" | "desc" = "desc"
) {
  try {
    const invitationsRef = collection(db, INVITATIONS_COLLECTION);
    let q = query(
      invitationsRef,
      orderBy(sortField, sortDirection),
      limit(pageSize)
    );
    
    if (lastVisible) {
      q = query(
        invitationsRef,
        orderBy(sortField, sortDirection),
        startAfter(lastVisible),
        limit(pageSize)
      );
    }
    
    const querySnapshot = await getDocs(q);
    const invitations: UserInvitation[] = [];
    let lastDoc = null;
    
    querySnapshot.forEach((doc) => {
      const invitation = {
        id: doc.id,
        ...doc.data()
      } as UserInvitation;
      
      invitations.push(invitation);
      
      // Cache individual invitations
      CacheService.set(`invitation:${doc.id}`, invitation, INVITATION_CACHE_TTL);
      
      lastDoc = doc;
    });
    
    return {
      invitations,
      lastVisible: lastDoc,
      hasMore: invitations.length === pageSize
    };
  } catch (error) {
    console.error("Error getting paginated invitations:", error);
    throw error;
  }
}

// Export the deprecated function name for backward compatibility
export const clearLastViewedTime = clearViewHistory;
